import styles from './askAnything.module.css';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

import { type AskChannel, type AskScenario } from './askScenarios';
import { type AnswerToken, type AskDemoState } from './useAskDemo';

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

const CHANNEL_ICONS: Record<AskChannel, React.ReactNode> = {
  web: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" />
      <path d="M1.8 8h12.4M8 1.8c1.8 1.9 2.6 3.9 2.6 6.2S9.8 12.3 8 14.2C6.2 12.3 5.4 10.3 5.4 8S6.2 3.7 8 1.8z" />
    </svg>
  ),
  slack: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true">
      <path d="M6 2.5 5 13.5M11 2.5l-1 11M2.5 6h11M2 10.5h11" />
    </svg>
  ),
  whatsapp: (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M8 1.8a6.2 6.2 0 0 0-5.4 9.3L1.8 14.2l3.2-.8A6.2 6.2 0 1 0 8 1.8z" />
    </svg>
  ),
  workspace: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <rect x="1.8" y="2.5" width="12.4" height="9" rx="1.6" />
      <path d="M5.5 14h5M8 11.5V14" />
    </svg>
  ),
};

export const Chip: React.FC<{ n: number; pop?: boolean }> = ({ n, pop }) => (
  <span className={cx(styles.chip, pop && styles.chipPop)}>{n}</span>
);

function statusOf(scenario: AskScenario, state: AskDemoState) {
  switch (state.phase) {
    case 'reading':
      return {
        cls: styles.statusReading,
        text: `Reading · ${scenario.reading[state.readingStep] ?? ''}`,
      };
    case 'found':
    case 'answering':
      return { cls: styles.statusFound, text: scenario.found };
    case 'done':
      return { cls: styles.statusDone, text: `✓ ${scenario.outcome}` };
    default:
      return { cls: undefined, text: 'Listening' };
  }
}

function renderTokens(tokens: AnswerToken[], shown: number, animate: boolean) {
  return tokens.slice(0, shown).map((token, i) =>
    token.kind === 'cite' ? (
      // eslint-disable-next-line react/no-array-index-key -- tokens are positional and never reorder
      <span key={i}>
        <Chip n={token.n} pop={animate} />
        {token.trail}{' '}
      </span>
    ) : (
      // eslint-disable-next-line react/no-array-index-key -- tokens are positional and never reorder
      <span key={i} className={animate ? styles.word : undefined}>
        {token.text}{' '}
      </span>
    )
  );
}

interface AskPanelProps {
  scenario: AskScenario;
  tokens: AnswerToken[];
  state: AskDemoState;
  animate: boolean;
  userPaused: boolean;
  onTogglePause: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
  panelHandlers: React.HTMLAttributes<HTMLDivElement>;
}

const AskPanel: React.FC<AskPanelProps> = ({
  scenario,
  tokens,
  state,
  animate,
  userPaused,
  onTogglePause,
  panelRef,
  panelHandlers,
}) => {
  const status = statusOf(scenario, state);
  const reading = state.phase === 'reading';
  const answered = ['found', 'answering', 'done'].includes(state.phase);
  const scanChip = reading ? state.readingStep % scenario.knowledge.length : -1;

  return (
    <div
      ref={panelRef}
      className={cx(styles.prompt, styles.enter)}
      style={{ '--delay': '.75s' } as React.CSSProperties}
      {...panelHandlers}>
      <div className={cx(styles.promptIn, state.phase === 'swap' && styles.swapOut)}>
        <div className={styles.who}>
          <span
            className={cx(styles.avatar, styles.fades)}
            style={{ background: scenario.avatarColor }}
            aria-hidden="true">
            {scenario.avatar}
          </span>
          <span className={styles.fades} aria-hidden="true">
            <b>{scenario.askerName}</b> {scenario.askerContext}
          </span>
          <span className={cx(styles.channel, styles.fades)} aria-hidden="true">
            {CHANNEL_ICONS[scenario.channel]}
            <span className={styles.channelLabel}>{scenario.channelLabel}</span>
          </span>
          {animate && (
            <button
              type="button"
              className={styles.ctrl}
              data-demo-control="pause"
              aria-pressed={userPaused}
              aria-label={userPaused ? 'Play demo' : 'Pause demo'}
              onClick={onTogglePause}>
              {userPaused ? (
                <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                  <path d="M3 1.8v8.4c0 .5.5.8.9.5l6.6-4.2c.4-.2.4-.8 0-1L3.9 1.3c-.4-.3-.9 0-.9.5z" />
                </svg>
              ) : (
                <svg viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
                  <rect x="2" y="1.5" width="2.6" height="9" rx="1" />
                  <rect x="7.4" y="1.5" width="2.6" height="9" rx="1" />
                </svg>
              )}
            </button>
          )}
        </div>

        <p
          className={cx(styles.question, styles.fades, state.phase === 'typing' && styles.typing)}
          aria-hidden="true">
          {scenario.question.slice(0, state.typed)}
          <span className={styles.caret} />
        </p>

        <div className={styles.rule} />

        <div className={cx(styles.body, styles.fades)} aria-hidden="true">
          <div className={cx(styles.status, status.cls)}>
            <span className={styles.dot} />
            <span>{status.text}</span>
          </div>
          <div className={styles.answer}>{renderTokens(tokens, state.shownTokens, animate)}</div>
          {state.sourcesShown && (
            <div className={styles.sources}>
              {scenario.sources.map((src, i) => (
                <span
                  key={src.name}
                  className={styles.src}
                  style={{ animationDelay: animate ? `${i * 0.14}s` : '0s' }}>
                  <Chip n={i + 1} />
                  {src.name} <span className={styles.srcLoc}>{src.location}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.foot}>
          <span className={styles.footLabel}>Knows</span>
          <KnowledgeChips
            items={scenario.knowledge}
            scan={scanChip}
            hit={answered ? scenario.knowledgeHit : -1}
            smooth={animate}
          />
          <Link href={PageLink.login} className={styles.send}>
            Try it on your docs <span className={styles.arrow}>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

/** On phones the chips sit in a swipeable strip; the active chip is slid into view. */
const KnowledgeChips: React.FC<{ items: string[]; scan: number; hit: number; smooth: boolean }> = ({
  items,
  scan,
  hit,
  smooth,
}) => {
  const stripRef = useRef<HTMLSpanElement>(null);
  const active = hit >= 0 ? hit : scan;

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
    const chip = active >= 0 ? (strip.children[active] as HTMLElement | undefined) : undefined;
    const left = chip ? Math.max(0, chip.offsetLeft - strip.offsetLeft - 16) : 0;
    strip.scrollTo({ left, behavior: chip && smooth ? 'smooth' : 'auto' });
  }, [active, items, smooth]);

  return (
    <span ref={stripRef} className={cx(styles.kbs, styles.fades)} aria-hidden="true">
      {items.map((item, i) => (
        <span
          key={item}
          className={cx(styles.kb, i === scan && styles.kbScan, i === hit && styles.kbHit)}>
          <i />
          {item}
        </span>
      ))}
    </span>
  );
};

export default AskPanel;
